/*
 * UwU Rewards — membership choice on the cart page.
 *
 * Selecting "UwU Rewards" adds a real cart line carrying the membership selling plan.
 * Selecting "Pay as Guest" removes it. Both record the choice as a cart attribute
 * (_uwu_rewards_choice) so an opt-out survives a page reload and is visible to the app.
 *
 * Notes for anyone changing this:
 *  - Every listener is delegated from `document`, because the cart footer's .js-contents is
 *    replaced wholesale by Dawn's own quantity-update path (assets/cart.js) and by refresh()
 *    below. Direct listeners would be destroyed on the first re-render.
 *  - Adding the line goes through /cart/add.js. Shipping protection is intentionally not added
 *    here; the shopper chooses protected or unprotected checkout after the cart is complete.
 *    The cart is re-read after the mutation so every rendered membership choice uses server state.
 */
(function () {
  'use strict';

  // Run once per document, however many times this file is emitted.
  //
  // Two <script> tags with the same src execute TWICE — classic scripts are not deduplicated. On
  // /cart that produced two module instances, each with its own closure `inFlight` and its own
  // document-level change delegate, neither able to see the other. One trusted click on the
  // membership radio therefore ran choose('member') twice, ~292ms apart and never concurrently, so
  // `inFlight` never fired: the shopper got a subscription line at quantity 2. It also doubled
  // maybeAutoAdd, cleanUpLegacyAttribute and every refresh() section fetch.
  //
  // The emitter is now single (snippets/cart-drawer.liquid), but this guard is the actual fix. A
  // second emitter is a two-line copy-paste that reads as obviously correct in review, and the one
  // that caused this shipped in #81 and went unnoticed for weeks. A guard that depends on
  // remembering is not a guard — the same reasoning that put the row hiding in CSS in #86.
  //
  // Safe because the module is document-scoped, not element-scoped: roots() collects every copy,
  // the change delegate is on document, and refresh() targets sections by id. One instance already
  // serves both the drawer and the /cart page.
  if (window.__uwuCartMembershipLoaded) return;
  window.__uwuCartMembershipLoaded = true;

  var ROOT_SELECTOR = '[data-uwu-rewards]';
  var CHOICE_SELECTOR = '[data-uwu-rewards-choice]';

  // Records the EVENT "the membership was offered on this cart". Write-once; nothing sets it back.
  // See snippets/cart-membership.liquid for why this is an event and not a state.
  var OFFERED_KEY = '_uwu_rewards_offered';
  // Superseded. Carried only so carts created before this change get it cleared off their payload.
  var LEGACY_KEY = '_uwu_rewards_choice';

  var inFlight = false;

  // There can be TWO copies of the choice in one document: the visible one on /cart and the one
  // inside the cart drawer, which the layout renders on every page. They carry namespaced ids and
  // radio groups (see snippets/cart-membership.liquid id_prefix), so anything acting on a shopper's
  // click must act on the copy that click came from, not on whichever copy happens to be first in
  // the DOM. Both copies hold the same variant, so the mutation is identical either way; what
  // differs is which copy shows the busy state and the status message.
  // ⚠️ On /cart the drawer copy comes FIRST in the DOM: layout/theme.liquid renders the drawer
  // (:569) above content_for_layout (:585). So a bare document.querySelector for this block
  // returns the copy nobody can see, inside a closed drawer. Every lookup here therefore prefers
  // the copy that is NOT in a drawer, and only falls back to the drawer's when that is the only
  // one there is - which is the case on every page except /cart.
  function roots() {
    return [].slice.call(document.querySelectorAll(ROOT_SELECTOR));
  }

  function preferVisible(list) {
    var onPage = list.filter(function (el) {
      return !el.closest('cart-drawer');
    });
    return onPage[0] || list[0] || null;
  }

  function root() {
    return preferVisible(roots());
  }

  function rootFor(element) {
    return (element && element.closest && element.closest(ROOT_SELECTOR)) || root();
  }

  // Dawn's cart drawer redraws itself from `/cart?section_id=cart-drawer` when a cartUpdate is
  // published (assets/cart.js onCartUpdate, skipped for source 'cart-items'). refresh() below only
  // rebuilds the /cart page sections, which do not exist when the drawer is used from a product
  // page, so without this the drawer would keep showing the membership line it had just removed.
  function announceCartUpdate() {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'uwu-rewards-membership' });
      }
    } catch (error) {
      /* the drawer redraw is a nicety; never let it break the mutation that already succeeded */
    }
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (response) {
      if (!response.ok) throw new Error(url + ' responded ' + response.status);
      return response.json();
    });
  }

  // Empty string is how the Ajax API deletes a cart attribute, so every write also drops the
  // superseded key rather than leaving it to ride along to the order.
  function offeredAttributes() {
    var attributes = {};
    attributes[OFFERED_KEY] = 'true';
    attributes[LEGACY_KEY] = '';
    return attributes;
  }

  function markOffered() {
    return post('/cart/update.js', { attributes: offeredAttributes() });
  }

  /* ---------- section re-render, mirroring assets/cart.js getSectionsToRender ---------- */

  function sectionTargets() {
    var targets = [];
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');

    if (items) targets.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    if (footer) targets.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    if (document.getElementById('cart-live-region-text')) {
      targets.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }
    return targets;
  }

  function innerHTMLFor(html, selector) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var found = parsed.querySelector(selector);
    return found ? found.innerHTML : null;
  }

  function refresh() {
    var targets = sectionTargets();
    if (!targets.length) return Promise.resolve();

    var names = targets
      .map(function (target) {
        return target.section;
      })
      .join(',');

    return Promise.all([
      fetch('/cart.js?ts=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' }).then(function (r) {
        return r.json();
      }),
      fetch('/cart?sections=' + encodeURIComponent(names), {
        credentials: 'same-origin',
        cache: 'no-store',
      }).then(function (r) {
        return r.json();
      }),
    ]).then(function (results) {
      var cart = results[0];
      var sections = results[1];
      var isEmpty = cart.item_count === 0;

      targets.forEach(function (target) {
        var host = document.getElementById(target.id);
        var markup = sections[target.section];
        if (!host || typeof markup !== 'string') return;

        var replaceIn = host.querySelector(target.selector) || host;
        var html = innerHTMLFor(markup, target.selector);
        if (html !== null) replaceIn.innerHTML = html;
      });

      var footer = document.getElementById('main-cart-footer');
      var cartItems = document.querySelector('cart-items');
      if (footer) footer.classList.toggle('is-empty', isEmpty);
      if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);

      // The PROTECTED CHECKOUT label is rewritten client-side; force it after a re-render so it
      // does not sit on a stale total.
      if (window.updateProtectedCheckoutLabels) {
        try {
          window.updateProtectedCheckoutLabels(true);
        } catch (error) {
          /* label refresh is cosmetic; never let it break the flow */
        }
      }
    });
  }

  /* ---------- state ---------- */

  function setBusy(busy, message, instance) {
    var host = instance || root();
    if (!host) return;
    host.classList.toggle('uwu-rewards--busy', !!busy);
    host.querySelectorAll(CHOICE_SELECTOR).forEach(function (input) {
      input.disabled = !!busy;
    });
    var status = host.querySelector('[data-uwu-rewards-status]');
    if (status) status.textContent = message || '';
  }

  function addMembership(host) {
    var variantId = Number(host.dataset.variantId);
    var sellingPlanId = Number(host.dataset.sellingPlanId);
    if (!variantId) return Promise.reject(new Error('UwU Rewards: no variant configured'));

    var body = { id: variantId, quantity: 1 };
    if (sellingPlanId) body.selling_plan = sellingPlanId;

    return post('/cart/add.js', body);
  }

  function removeMembership(host) {
    var variantId = Number(host.dataset.variantId);
    if (!variantId) return Promise.reject(new Error('UwU Rewards: no variant configured'));

    // Keyed by variant id rather than line key so a stale key from a previous render can never
    // remove the wrong line. Setting attributes in the same request keeps it to one round trip.
    var updates = {};
    updates[variantId] = 0;

    return post('/cart/update.js', { updates: updates, attributes: offeredAttributes() }).then(function (result) {
      // Protection is selected at checkout, so membership changes only refresh the projected total.
      try { window.updateProtectedCheckoutLabels && window.updateProtectedCheckoutLabels(true); } catch (_) {}
      return result;
    });
  }

  function choose(value, instance) {
    var host = instance || root();
    if (!host || inFlight) return;

    inFlight = true;
    setBusy(
      true,
      value === 'member' ? 'Adding UwU Rewards membership' : 'Removing UwU Rewards membership',
      host
    );

    // "Offered" is recorded BEFORE the line is added, never after. If the add fails — wrong
    // variant id for this store, sold out, an order-limit app refusing it — the cart still
    // remembers that it was offered, so the next page view shows Pay as Guest instead of firing
    // the same doomed request again on every single load.
    var action =
      value === 'member'
        ? markOffered().then(function () {
            return addMembership(host);
          })
        : removeMembership(host);

    action
      .then(refresh)
      .catch(function (error) {
        console.warn('[UwU Rewards] cart update failed', error);
        // Put the UI back on whatever the cart actually says rather than leaving it lying.
        return refresh().catch(function () {});
      })
      .then(function () {
        inFlight = false;
        setBusy(false, '', host);
        // Last, and outside the mutation: the drawer redraw replaces the element this very
        // handler is attached to, so it has to happen after the busy state is cleared.
        announceCartUpdate();
      });
  }

  /* ---------- wiring ---------- */

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches(CHOICE_SELECTOR)) return;
    if (!input.checked) return;
    choose(input.value, rootFor(input));
  });

  function maybeAutoAdd() {
    if (inFlight) return;

    // Both copies of the choice can render with auto-add set, so EVERY copy is marked handled
    // before the single add is started. Marking only the one being acted on would leave the other
    // armed, and one shopper would get two membership lines.
    var hosts = [].slice.call(document.querySelectorAll(ROOT_SELECTOR));
    var armed = hosts.filter(function (host) {
      return host.dataset.autoAdd === 'true';
    });
    if (!armed.length) return;

    hosts.forEach(function (host) {
      host.dataset.autoAdd = 'false';
    });
    choose('member', armed[0]);
  }

  // A cart created before this change carries the superseded _uwu_rewards_choice attribute, which
  // could say "member" on a cart the membership had already left — a false signal on a real order.
  // Liquid already reads it as "this cart was offered the membership", so the only thing left is to
  // take it off the payload. One write, only for carts that still have it.
  function cleanUpLegacyAttribute() {
    if (inFlight) return;

    // Every carrier is marked handled, not just the first one found. The first is the hidden
    // drawer copy on /cart (see roots() above), so clearing only that would leave the visible
    // copy still advertising a superseded attribute, and this guard reading whichever copy the
    // next render happened to put first.
    var carriers = [].slice.call(document.querySelectorAll('[data-legacy-choice]'));
    var pending = carriers.filter(function (el) {
      return el.dataset.legacyChoice;
    });
    if (!pending.length) return;

    pending.forEach(function (el) {
      el.dataset.legacyChoice = '';
    });
    inFlight = true;
    markOffered()
      .then(refresh)
      .catch(function (error) {
        console.warn('[UwU Rewards] could not clear the superseded cart attribute', error);
      })
      .then(function () {
        inFlight = false;
      });
  }

  function start() {
    // Order matters: the legacy attribute means "already offered", so Liquid has already set
    // data-auto-add to false. Clearing it can never cause an add.
    cleanUpLegacyAttribute();
    maybeAutoAdd();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
